import { useState, useEffect } from 'react';

import { Handle, Position, useNodeId, useUpdateNodeInternals } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import NodeIcon from '../../components/NodeIcon';
import NodeBodyWithDefaultConnection from '../NodeBodyWithDefaultConnection';
import NodeHeader from '../NodeHeader';
import NodeWrapper from '../NodeWrapper';

import type { QueueType } from './QueueControls';
import type { NodeProps } from '@xyflow/react';
import type { GetOneResult } from 'react-admin';

export default function QueueNode({ data }: NodeProps & { data: QueueType }): React.ReactElement {
  const [queue, setQueue] = useState<GetOneResult['data'] | null>(null);
  const translate = useTranslate();
  const nodeId = useNodeId();
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (data.queue !== undefined && data.queue !== null) {
        setQueue(data.queue);
      } else {
        setQueue(null);
      }
    };
    void fetchData();
  }, [data.queue]);

  useEffect(() => {
    // Update node internals when instructions change or sorted with dnd
    if (nodeId !== null) {
      updateNodeInternals(nodeId);
    }
  }, [nodeId, data.instructions, updateNodeInternals]);

  const errors = data.queue === undefined || data.queue === null ? ['callflows.nodes.queue.queueErrorEmpty'] : [];

  const handles =
    data.instructions === undefined ||
    data.instructions.length === 0 ||
    data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) === undefined
      ? []
      : data.instructions.map((instruction) => {
          return {
            id: instruction.id,
            value:
              typeof instruction.description === 'string' && instruction.description.length > 0
                ? instruction.description
                : instruction.match_exit_causes
                    .map((cause) =>
                      translate(`callflows.components.sortableInstructionsList.match_exit_causes.${cause}`)
                    )
                    .join(', '),
          };
        });

  return (
    <NodeWrapper errors={errors}>
      <NodeHeader
        icon={<NodeIcon name='queue' />}
        title={queue?.name !== undefined ? queue.name : translate('callflows.nodes.queue.name')}
        referencedResource={queue?.id !== undefined ? { resource: 'queues', id: queue.id } : undefined}
      />
      <Handle type='target' position={Position.Left} id='queue-target' />
      <NodeBodyWithDefaultConnection handles={handles} type={'queue'} />
    </NodeWrapper>
  );
}
